import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { FormField, Input } from '@/components/ui/Input'
import { typo } from '@/lib/typo'

export type TelegramLoginPhase = 'idle' | 'sending' | 'code' | 'verifying'

export interface TelegramLoginViewProps {
  phase: TelegramLoginPhase
  /** the server's answer in Russian (lib/telegram miniappErrorText) */
  error?: string | null
  /** the error ends this attempt (disabled, not an operator, no 2FA): no «Войти» again */
  fatal?: boolean
  onStart: () => void
  onVerify: (code: string) => void
  /** «Войти по email» */
  onUseEmail?: () => void
}

/**
 * «Вход через Telegram» (the admin opened as a Mini App, docs/admin-miniapp.md): Telegram vouches
 * for who opens the panel, then the 6-digit 2FA code is ALWAYS asked — for every role. The signed
 * initData is single-use, so an attempt that ran out (expired challenge, three wrong codes) ends
 * with «откройте админку из бота снова» instead of a retry that cannot work.
 */
export function TelegramLoginView({ phase, error, fatal, onStart, onVerify, onUseEmail }: TelegramLoginViewProps) {
  const [code, setCode] = useState('')
  const coding = phase === 'code' || phase === 'verifying'

  function submit(e: FormEvent) {
    e.preventDefault()
    if (code.length === 6) onVerify(code)
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-title-1 font-semibold text-ink-1">{typo('Вход через Telegram')}</h1>
      {!coding ? (
        <>
          <p className="text-body text-ink-2">
            {typo('Telegram подтвердит, кто вы. Затем понадобится код 2FA из приложения-аутентификатора.')}
          </p>
          {error && (
            <p className="text-caption text-danger" role="alert">
              <span aria-hidden>✕ </span>
              {error}
            </p>
          )}
          {!fatal && (
            <Button variant="primary" size="lg" full loading={phase === 'sending'} loadingLabel="Проверяю…" onClick={onStart}>
              Продолжить
            </Button>
          )}
        </>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-body text-ink-2">{typo('Telegram подтвердил вход. Введите код 2FA.')}</p>
          <FormField label="Код 2FA" hint="Шесть цифр из приложения-аутентификатора" error={error ?? undefined}>
            {(f) => (
              <Input
                {...f}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                pattern="[0-9]{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="tnum"
                required
              />
            )}
          </FormField>
          <Button type="submit" variant="primary" size="lg" full loading={phase === 'verifying'} loadingLabel="Вхожу…" disabled={code.length !== 6}>
            Войти
          </Button>
        </form>
      )}
      {onUseEmail && (
        <Button type="button" variant="ghost" size="sm" onClick={onUseEmail}>
          Войти по email
        </Button>
      )}
    </div>
  )
}
